interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showText?: boolean
  className?: string
}

const sizes = {
  sm: { icon: 28, title: 'text-base', sub: 'text-xs' },
  md: { icon: 40, title: 'text-xl', sub: 'text-sm' },
  lg: { icon: 56, title: 'text-3xl', sub: 'text-base' },
  xl: { icon: 80, title: 'text-5xl', sub: 'text-xl' },
}

export function BellIcon({ size = 40, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      {/* Bell body */}
      <path
        d="M24 6C24 6 14 10 14 22V32H10V36H38V32H34V22C34 10 24 6 24 6Z"
        fill="#13629B"
      />
      {/* Bell clapper */}
      <circle cx="24" cy="40" r="3" fill="#0CA8DD" />
      {/* Bell highlight */}
      <path
        d="M24 6C24 6 28 9 30 14C28.5 12 26.5 10.5 24 10C24 10 24 7.5 24 6Z"
        fill="#0CA8DD"
        opacity="0.6"
      />
      {/* Wireless signal arcs (call indicator) */}
      <path
        d="M36 16C37.5 18.5 38 21 38 24"
        stroke="#0CA8DD"
        strokeWidth="2.5"
        strokeLinecap="round"
        opacity="0.7"
      />
      <path
        d="M39.5 13C42 16.5 43 20 43 24"
        stroke="#0CA8DD"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.4"
      />
      <path
        d="M12 16C10.5 18.5 10 21 10 24"
        stroke="#0CA8DD"
        strokeWidth="2.5"
        strokeLinecap="round"
        opacity="0.7"
      />
      <path
        d="M8.5 13C6 16.5 5 20 5 24"
        stroke="#0CA8DD"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.4"
      />
    </svg>
  )
}

export function FaviconSvg() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="48" height="48" rx="10" fill="#13629B" />
      <path
        d="M24 8C24 8 15 12 15 23V32H11V36H37V32H33V23C33 12 24 8 24 8Z"
        fill="white"
      />
      <circle cx="24" cy="40" r="3" fill="#0CA8DD" />
      <path
        d="M35 14C36.5 16.5 37 19.5 37 23"
        stroke="#0CA8DD"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M13 14C11.5 16.5 11 19.5 11 23"
        stroke="#0CA8DD"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function DrAppMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="drapp-bg" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#13629B"/>
          <stop offset="100%" stopColor="#0CA8DD"/>
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="8" fill="url(#drapp-bg)"/>
      <rect x="6" y="19" width="4" height="7" rx="1.2" fill="white" opacity="0.85"/>
      <rect x="12" y="14" width="4" height="12" rx="1.2" fill="white" opacity="0.9"/>
      <rect x="18" y="9" width="4" height="17" rx="1.2" fill="white" opacity="0.95"/>
      <rect x="24" y="6" width="4" height="20" rx="1.2" fill="white"/>
      <path d="M6 17 C10 13, 16 10, 28 5" stroke="white" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.6"/>
    </svg>
  )
}

export function Logo({ size = 'md', showText = true, className = '' }: LogoProps) {
  const s = sizes[size]
  const drappSize = Math.round(s.icon * 0.55)

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="relative">
        <BellIcon size={s.icon} />
        <div
          className="absolute -bottom-1 -right-1 rounded-full"
          style={{ background: 'var(--card, white)', padding: 1 }}
        >
          <DrAppMark size={drappSize} />
        </div>
      </div>
      {showText && (
        <div className="flex flex-col leading-none">
          <span
            className={`font-bold text-foreground ${s.title}`}
            style={{ letterSpacing: '-0.02em' }}
          >
            Llamador
          </span>
          <span
            className={`font-semibold ${s.sub}`}
            style={{ color: 'var(--primary)' }}
          >
            DrApp
          </span>
        </div>
      )}
    </div>
  )
}
